/**
 * The project page's tabs: one table, read for the order, the icon and the word.
 *
 * These used to be three lists in `ProjectDetail`. A `Tab` union named the six,
 * a `TAB_ICON` record gave each one its glyph, and the row that PillTabs
 * actually draws was written out a third time inside the component with its
 * labels typed by hand. Three mirrors of one list means adding a tab is three
 * places to remember and at least one to forget, which is exactly the shape of
 * work that R4 is about to do three times over.
 *
 * The `Record<Tab, ...>` did buy something real, and it is worth naming because
 * it is the one property this table has to keep: a name added to the union
 * without an icon was a compile error, so the icon could not be forgotten.
 * Nothing made the other two halves an error. A tab left out of the label array
 * simply never appeared in the strip; a tab put in a different position there
 * sat somewhere else than its icon suggested; and a mistyped i18n key rendered
 * as a raw key rather than as a word. The type checker reads all three as
 * perfectly good code, and all three only surface when somebody opens the page
 * and looks at it.
 *
 * So order, icon and i18n key are ONE row here, and the union is derived from
 * the rows instead of being written beside them. Adding a tab is one line, and
 * a line cannot be half added.
 */
import {
  ChartGantt, Clock, FileSignature, Flag, FolderKanban, History, KanbanSquare, KeyRound, LifeBuoy,
  ListTodo, Paperclip, Receipt, Wallet, Workflow, type LucideIcon,
} from "lucide-react";
import type { ProjectSummary } from "../../../lib/projects";

/** One tab: what the URL calls it, what it is drawn with, what it is called. */
export interface ProjectTabDef {
  /** The value in the query string, and the tab's identity everywhere else. */
  id: string;
  icon: LucideIcon;
  /** The whole key rather than a suffix appended to a prefix, so a tab whose
   *  wording does not happen to live under `projects.tab.<id>` can still say
   *  where it does live instead of forcing the naming scheme onto the catalogue. */
  labelKey: string;
}

/**
 * The tabs, in the order the strip draws them.
 *
 * `as const` so the ids stay literals and the `Tab` union below can be read off
 * them; `satisfies` so the rows are still checked against the shape rather than
 * being whatever was typed. Both are needed: `as const` alone would let a
 * missing icon through, and the annotation alone would widen every id to
 * `string` and leave the union with nothing to derive from.
 */
export const PROJECT_TABS = [
  { id: "overview", icon: FolderKanban, labelKey: "projects.tab.overview" },
  { id: "roadmap", icon: KanbanSquare, labelKey: "projects.tab.roadmap" },
  // Beside the roadmap and not beside the milestones, because the two are the
  // same rows drawn twice: the board says which bucket a phase is in, the
  // timeline says when it happens and how long it takes. An operator comparing
  // "what is coming up" with "does this plan fit" should not have to cross the
  // whole strip to do it. `ChartGantt` and not a calendar glyph - a calendar
  // means a date, and this tab is about durations.
  { id: "gantt", icon: ChartGantt, labelKey: "projects.tab.gantt" },
  // The third drawing of the plan, so it joins the other two: the board says
  // which bucket, the timeline says when, the flow map says in what order and
  // through which decisions. Unlike them it is the team's own drawing rather
  // than the milestone rows redrawn, which is why it carries no count below.
  { id: "flow", icon: Workflow, labelKey: "projects.tab.flow" },
  { id: "tasks", icon: ListTodo, labelKey: "projects.tab.tasks" },
  { id: "milestones", icon: Flag, labelKey: "projects.tab.milestones" },
  { id: "time", icon: Clock, labelKey: "projects.tab.time" },
  { id: "invoices", icon: Receipt, labelKey: "projects.tab.invoices" },
  // The three linked registers, and they sit AFTER the money rather than before
  // it because the strip is read left to right from the work to what the work
  // cost: the plan, the tasks, the phases, the hours, what was billed, and then
  // the records that hang off the same project without being the work itself.
  // All three are money-adjacent in the same way - a cost, an agreement about
  // cost, and the support a paid engagement carries - so they belong in one run
  // rather than scattered between the delivery tabs.
  //
  // Each icon is also the icon its own tab draws in its empty card, so a project
  // that has no contracts shows the same glyph twice rather than teaching two
  // shapes for one idea. `Receipt` was already spent on invoices, which is why
  // expenses gets `Wallet` and not the obvious receipt.
  { id: "expenses", icon: Wallet, labelKey: "projects.tab.expenses" },
  { id: "contracts", icon: FileSignature, labelKey: "projects.tab.contracts" },
  { id: "tickets", icon: LifeBuoy, labelKey: "projects.tab.tickets" },
  // The documents, after the three registers and before the timeline. They are
  // not a register - nothing here links an existing record to the project, the
  // rows only exist because somebody put a file on this project - so they sit
  // outside that run rather than inside it. `Paperclip` because it is what the
  // mail composer already uses for a file, and an operator should not have to
  // learn a second glyph for the same idea in the same product.
  { id: "files", icon: Paperclip, labelKey: "projects.tab.files" },
  // The credentials, between the documents and the timeline. Like the files
  // they are not a linked register - a vault entry is written in the vault and
  // merely says which project it belongs to - and like the files they are a
  // thing somebody put HERE rather than a record that happens to name this
  // project. `KeyRound` is the glyph the vault register already draws for an
  // entry of no particular kind, so the strip and that page agree.
  //
  // The one tab on this page that can be absent. It is drawn only for an
  // operator who holds `read:crm.vault`; everybody else gets ten tabs and no
  // hint that an eleventh exists. See `ProjectDetail`, which does the
  // filtering - the table stays the full list, because a table that answered
  // permission questions would have to be handed a user to be read at all.
  { id: "vault", icon: KeyRound, labelKey: "projects.tab.vault" },
  // Last, and after the linked registers rather than among them, because it is
  // the only tab that is ABOUT the other eleven. The timeline is what an
  // operator opens when the figures above have already told them something is
  // odd and they need to know what happened - so it reads as the end of the
  // strip, the way a history section reads as the end of a document.
  { id: "activity", icon: History, labelKey: "projects.tab.activity" },
] as const satisfies readonly ProjectTabDef[];

/** The open tab, as the rest of the page passes it around. Derived from the
 *  table above, so there is no second list of names that can disagree with it. */
export type Tab = (typeof PROJECT_TABS)[number]["id"];

/** Which query string the tab lives in. */
export const TAB_PARAM = "tab";

/**
 * Is this string one of ours?
 *
 * Asked against the table rather than a hand-written array of names, for the
 * same reason everything else here is: a tab is accepted by the URL the moment
 * its row exists, and a row is the only thing there is to add.
 */
export function isTab(v: string | null): v is Tab {
  return v !== null && PROJECT_TABS.some((tab) => tab.id === v);
}

/**
 * Which tabs carry a number on the pill, and which figure it is.
 *
 * Twelve of the fourteen. Every tab that has a countable set behind it says how
 * big that set is, because the strip is a rail now: eleven of the pills are off
 * screen or half read at any moment, and a number is the only thing that tells
 * an operator which of them is worth scrolling to. Only the overview and the
 * flow map are left bare: there is no set behind the overview to count, and the
 * flow map is one drawing whose size lives in its own document, not in the
 * summary.
 *
 * A figure that is ALSO in the KPI strip is still drawn here. That was the old
 * rule's one exception and it cost more than it saved: the tasks pill stayed
 * blank on a project with forty tasks because the strip one card up printed the
 * OVERDUE ones, which is a different number answering a different question. A
 * pill says how much is behind it. The strip says how the project is going.
 *
 * Functions rather than a key into `linkedCounts`, because half of these are not
 * in it: the tasks total, the milestone rollup, the invoices and the estimates
 * all travel as their own fields, and the alternative was six more integers on
 * the server duplicating arrays the response already carries.
 *
 * Deliberately partial rather than a full `Record<Tab, ...>`: a tab with nothing
 * to count must not be forced to invent a figure. The trade is worth saying out
 * loud - a tab that SHOULD carry a count and is left out of this map is not a
 * compile error, it just draws a bare pill.
 */
const TAB_COUNT: Partial<Record<Tab, (s: ProjectSummary) => number>> = {
  // The roadmap, the timeline and the milestone list are the same rows drawn
  // three ways, so all three carry the same number - and that is the point
  // rather than a duplication to tidy up later. An operator who sees "4" on the
  // roadmap and nothing on the timeline would reasonably read it as two
  // different sets; three pills agreeing says plainly that there is one set of
  // four phases and three ways to look at it.
  //
  // `summary.milestones` is the per-phase task rollup, and its length is the
  // project's milestone count exactly: the server seeds one entry per milestone
  // before it counts anything, so a phase with no tasks is still a row (see
  // `rollUpMilestones`). Counting the array is not counting tasks.
  roadmap: (s) => s.milestones.length,
  gantt: (s) => s.milestones.length,
  milestones: (s) => s.milestones.length,
  // Every task, not the open ones and not the overdue ones. The pill introduces
  // the list the tab shows, and the tab shows all of them; a badge counting a
  // subset would disagree with the rows underneath it. The overdue figure has
  // its own plate in the KPI strip, where it belongs - it is a warning, not a
  // size.
  tasks: (s) => s.taskCounts.total,
  // Rows, not hours. The strip prints the hours one card up, and a pill saying
  // "480" over a list of four entries would read as a broken counter.
  time: (s) => s.linkedCounts.timeEntries,
  // Both kinds of document, because the tab lists both. An invoices badge that
  // counted only invoices would say "nothing here" on a project carrying two
  // quotes and no bill yet, which is the exact case an operator opens the tab
  // to check.
  invoices: (s) => s.invoices.length + s.estimates.length,
  expenses: (s) => s.linkedCounts.expenses,
  contracts: (s) => s.linkedCounts.contracts,
  // ALL tickets rather than `openTickets`. The badge says how much is behind the
  // pill, and the pill opens the whole list; a number that counted a subset
  // would disagree with the rows it introduces.
  tickets: (s) => s.linkedCounts.tickets,
  // The files count is the project's OWN rows only. Files hung off its tasks are
  // deliberately not added in on the server either (`lib/summary.ts`): a pill
  // promising eleven over a list of three is the kind of small lie that makes an
  // operator stop trusting the other counters too.
  files: (s) => s.linkedCounts.files,
  // The credentials. The pill is drawn only when the tab is (see above), so a
  // count reaching an operator without `read:crm.vault` has nowhere to be shown.
  vault: (s) => s.linkedCounts.vault,
  // The timeline is the one entry here whose figure is not a register's size. It
  // stays because the question the badge answers is the same one - is this tab
  // worth opening - and because a project with a history and a project with none
  // are genuinely different things to walk into. The number grows on its own,
  // which the registers do not, so it is worth watching: if the automatic event
  // set ever stops being four events and starts mirroring the audit log, this
  // pill is where it will first look wrong.
  activity: (s) => s.linkedCounts.activity,
};

/**
 * The number on a tab's pill, or nothing.
 *
 * Zero draws NOTHING, and that is the rule rather than a nicety. Most tabs are
 * empty on most projects - there are no contracts in the CRM at all, and not one
 * of production's tickets names a project - so a strip of "0" badges would be
 * the first thing every operator saw, and a row of zeroes reads as a counter
 * that failed rather than as registers nobody has filed anything in yet. The tab
 * still opens and its empty card still says so in words.
 *
 * Absent while the summary is in flight, which is the same silence for a
 * different reason: a badge that appeared as "0" and then changed to "7" would
 * have been wrong for as long as it was legible.
 */
export function tabCount(id: Tab, summary: ProjectSummary | null): number | undefined {
  const read = TAB_COUNT[id];
  if (!read || !summary) return undefined;
  const n = read(summary);
  return n > 0 ? n : undefined;
}
